import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaVideo, FaUsers, FaChartLine, FaApple, FaGooglePlay, FaTimes } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import appConfig from '../../config/appConfig';
import LanguageSelector from '../../components/LanguageSelector';

// Import TokLive logo
import tokLiveLogo from '../../assets/icons/ic_logo_white.png';

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2a6c, #b21f1f, #fdbb2d);
  color: white;
`;

const Header = styled.header`
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 2rem;
  font-weight: bold;
  
  img {
    height: 60px;
    margin-right: 10px;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  height: 40px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    
    a {
      width: 100%;
    }
    
    button {
      width: 100%;
    }
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.primary {
    background-color: white;
    color: #1a2a6c;
    border: none;
    min-width: 120px;
    
    &:hover {
      background-color: #f0f0f0;
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: white;
    border: 2px solid white;
    min-width: 120px;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
      transform: translateY(-2px);
    }
  }
`;

const HeroSection = styled.section`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  max-width: 800px;
  margin-bottom: 3rem;
  opacity: 0.9;
`;

const FeaturesSection = styled.section`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const FeatureCard = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 2rem;
  width: 280px;
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  opacity: 0.8;
`;

const Footer = styled.footer`
  padding: 2rem;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.2);
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  color: #333;
  text-align: left;
`;

const PopupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  h2 {
    color: #1a2a6c;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  
  &:hover {
    color: #333;
  }
`;

const AppButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  a {
    display: flex;
    flex: 1;
  }
  
  button {
    height: 48px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const WelcomePage = () => {
  const [showPopup, setShowPopup] = useState(false);
  const { t } = useTranslation();
  
  const openPopup = () => setShowPopup(true);
  const closePopup = () => setShowPopup(false);
  return (
    <WelcomeContainer>
      <Header>
        <Logo>
          <img src={tokLiveLogo} alt="TokLive Logo" />
        </Logo>
        <NavButtons>
          <LanguageSelector />
          <Link to="/login">
            <Button className="secondary">{t('auth.welcome.login')}</Button>
          </Link>
          <Button className="primary" onClick={openPopup}>{t('auth.welcome.getStarted')}</Button>
        </NavButtons>
      </Header>
      
      <HeroSection>
        <Title>{t('auth.welcome.heroTitle')}</Title>
        <Subtitle>
          {t('auth.welcome.heroSubtitle')}
        </Subtitle>
        
        <FeaturesSection>
          <FeatureCard>
            <FeatureIcon>
              <FaVideo />
            </FeatureIcon>
            <FeatureTitle>{t('auth.welcome.features.scheduling')}</FeatureTitle>
            <FeatureDescription>
              {t('auth.welcome.features.schedulingDesc')}
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>
              <FaUsers />
            </FeatureIcon>
            <FeatureTitle>{t('auth.welcome.features.hostManagement')}</FeatureTitle>
            <FeatureDescription>
              {t('auth.welcome.features.hostManagementDesc')}
            </FeatureDescription>
          </FeatureCard>
          
          <FeatureCard>
            <FeatureIcon>
              <FaChartLine />
            </FeatureIcon>
            <FeatureTitle>{t('auth.welcome.features.analytics')}</FeatureTitle>
            <FeatureDescription>
              {t('auth.welcome.features.analyticsDesc')}
            </FeatureDescription>
          </FeatureCard>
        </FeaturesSection>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <a href="https://apps.apple.com/app/toklive" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button className="secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaApple /> {t('auth.welcome.downloadPopup.downloadIos')}
              </Button>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.toklive" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button className="secondary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaGooglePlay /> {t('auth.welcome.downloadPopup.downloadAndroid')}
              </Button>
            </a>
          </div>
          <p style={{ color: 'white', marginBottom: '1rem' }}>
            {t('auth.welcome.downloadInstructions')}
          </p>
          <ButtonGroup>
            <Link to="/login">
              <Button className="primary" style={{ padding: '1rem 2.5rem', fontSize: '1.2rem' }}>
                {t('auth.welcome.accessDashboard')}
              </Button>
            </Link>
          </ButtonGroup>
        </div>
      </HeroSection>
      
      <Footer>
        {t('common.copyright', { year: new Date().getFullYear() })}
      </Footer>
      
      {showPopup && (
        <PopupOverlay onClick={closePopup}>
          <PopupContent onClick={(e) => e.stopPropagation()}>
            <PopupHeader>
              <h2>{t('auth.welcome.downloadPopup.title')}</h2>
              <CloseButton onClick={closePopup}>
                <FaTimes />
              </CloseButton>
            </PopupHeader>
            
            <p style={{ color: '#333' }}>{t('auth.welcome.downloadPopup.instructions')}</p>
            
            <ol style={{ margin: '1rem 0', paddingLeft: '1.5rem', color: '#333' }}>
              <li style={{ marginBottom: '0.5rem' }}>{t('auth.welcome.downloadPopup.step1')}</li>
              <li style={{ marginBottom: '0.5rem' }}>{t('auth.welcome.downloadPopup.step2')}</li>
              <li>{t('auth.welcome.downloadPopup.step3')}</li>
            </ol>
            
            <AppButtons>
              <a href={appConfig.appStoreLinks.ios} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                <button 
                  style={{ 
                    width: '100%', 
                    height: '48px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '0.5rem',
                    padding: '0 1.5rem',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'transparent',
                    color: '#1a2a6c',
                    border: '2px solid #1a2a6c'
                  }}
                >
                  <FaApple /> {t('auth.welcome.downloadPopup.downloadIos')}
                </button>
              </a>
              <a href={appConfig.appStoreLinks.android} target="_blank" rel="noopener noreferrer" style={{ flex: 1 }}>
                <button 
                  style={{ 
                    width: '100%', 
                    height: '48px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '0.5rem',
                    padding: '0 1.5rem',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: 'transparent',
                    color: '#1a2a6c',
                    border: '2px solid #1a2a6c'
                  }}
                >
                  <FaGooglePlay /> {t('auth.welcome.downloadPopup.downloadAndroid')}
                </button>
              </a>
            </AppButtons>
            
            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <p style={{ color: '#333' }}>{t('auth.welcome.downloadPopup.haveAccount')}</p>
              <Link to="/login">
                <button 
                  style={{ 
                    marginTop: '0.5rem',
                    height: '48px',
                    padding: '0 1.5rem',
                    borderRadius: '50px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    backgroundColor: '#1a2a6c',
                    color: 'white',
                    border: 'none',
                    width: '100%'
                  }}
                >
                  {t('auth.welcome.downloadPopup.loginToDashboard')}
                </button>
              </Link>
            </div>
          </PopupContent>
        </PopupOverlay>
      )}
    </WelcomeContainer>
  );
};

export default WelcomePage;
